import { IndicatorDetails } from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import {
  IndicatorSectionKey,
  indicatorSectionsInOrder,
} from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import { numberWithCommas, replaceCommas } from 'shared/utils/numberUtils';
import {
  getIndicatorDetailsMeasurementKey,
  getLegacyIndicatorDetailsMeasurementKey,
} from '../../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import { SectionIndicators } from '../../../../ReportBuilder/Components/IndicatorDetails/utils';

export const stripCommas = (value: string): string => value.replace(/,/gi, '');

export const getForecast = (
  isFinalForecast: boolean,
  indicator: IndicatorDetails,
): string => {
  return numberWithCommas(
    isFinalForecast ? indicator.finalForecast : indicator.forecast,
  );
};

export const findForecastMatches = (
  targets: IndicatorDetails[],
  value: string,
  getTargetValue: (t: IndicatorDetails) => number | string | undefined,
): IndicatorDetails[] => {
  const v = Number(value);
  if (!value || Number.isNaN(v)) return [];
  return targets.filter(t => {
    const raw = getTargetValue(t);
    if (raw === undefined || raw === null || raw === '') return false;
    const n = Number(String(raw).replace(/,/gi, ''));
    return !Number.isNaN(n) && n === v;
  });
};

export const CROSS_SECTION_FORECAST_CHECKS: Partial<Record<
  IndicatorSectionKey,
  IndicatorSectionKey
>> = {
  reach: 'capacityDevelopment',
  capacityDevelopment: 'reach',
};

export interface CrossSectionMatchInput {
  indicator: IndicatorDetails;
  targetIndicators: IndicatorDetails[];
  forecastValue: string;
  finalForecastValue: string;
  hideForecast: boolean;
  hideFinalForecast: boolean;
}

export interface CrossSectionMatchResult {
  forecastMatches: IndicatorDetails[];
  finalForecastMatches: IndicatorDetails[];
}

export const getCrossSectionForecastMatches = ({
  indicator,
  targetIndicators,
  forecastValue,
  finalForecastValue,
  hideForecast,
  hideFinalForecast,
}: CrossSectionMatchInput): CrossSectionMatchResult => {
  const forecastChanged =
    !hideForecast &&
    forecastValue !== stripCommas(getForecast(false, indicator));
  const finalForecastChanged =
    !hideFinalForecast &&
    finalForecastValue !== stripCommas(getForecast(true, indicator));

  return {
    forecastMatches: forecastChanged
      ? findForecastMatches(targetIndicators, forecastValue, t => t.forecast)
      : [],
    finalForecastMatches: finalForecastChanged
      ? findForecastMatches(
          targetIndicators,
          finalForecastValue,
          t => t.finalForecast,
        )
      : [],
  };
};

export interface IndicatorWithForecastValues {
  indicator: IndicatorDetails;
  forecastValue: string;
  finalForecastValue: string;
}

export interface BulkCrossSectionMatchInput {
  sourceIndicators: IndicatorWithForecastValues[];
  targetIndicators: IndicatorDetails[];
  hideForecast: boolean;
  hideFinalForecast: boolean;
}

export interface ReachForecastMatch {
  indicator: IndicatorDetails;
  forecastValue: string;
  finalForecastValue: string;
  forecastMatches: IndicatorDetails[];
  finalForecastMatches: IndicatorDetails[];
  level1Forecast: string;
}

export const getBulkCrossSectionForecastMatches = ({
  sourceIndicators,
  targetIndicators,
  hideForecast,
  hideFinalForecast,
}: BulkCrossSectionMatchInput): ReachForecastMatch[] => {
  const results: ReachForecastMatch[] = [];

  for (const {
    indicator,
    forecastValue,
    finalForecastValue,
  } of sourceIndicators) {
    // Directly check for matches without requiring value changes
    const forecastMatches =
      !hideForecast && forecastValue
        ? findForecastMatches(targetIndicators, forecastValue, t => t.forecast)
        : [];

    const finalForecastMatches =
      !hideFinalForecast && finalForecastValue
        ? findForecastMatches(
            targetIndicators,
            finalForecastValue,
            t => t.finalForecast,
          )
        : [];

    if (forecastMatches.length > 0 || finalForecastMatches.length > 0) {
      results.push({
        indicator,
        forecastValue,
        finalForecastValue,
        forecastMatches,
        finalForecastMatches,
        level1Forecast: indicator.level1Forecast || '',
      });
    }
  }

  return results;
};

export const isGuessMeasurement = (
  sectionKey: IndicatorSectionKey,
  measurement?: string,
): boolean => {
  if (!measurement) return false;
  const type = getIndicatorDetailsMeasurementKey(sectionKey, measurement);
  if (type === 'Guess') return true;
  // Also handle legacy measurement IDs
  const legacy = getLegacyIndicatorDetailsMeasurementKey(measurement);
  return legacy.measurement === 'Guess';
};

export interface GuessMeasurementResult {
  indicator: IndicatorDetails;
  sectionKey: IndicatorSectionKey;
  measurement: string;
  level1Guess: string;
}

export const getBulkGuessMeasurements = (
  impactDetails: SectionIndicators,
): GuessMeasurementResult[] => {
  const results: GuessMeasurementResult[] = [];
  for (const sectionKey of indicatorSectionsInOrder) {
    if (sectionKey === 'catalyzed') continue;
    const indicators = impactDetails[sectionKey] || [];
    for (const indicator of indicators) {
      if (isGuessMeasurement(sectionKey, indicator.measurement)) {
        results.push({
          indicator,
          sectionKey,
          measurement: indicator.measurement || '',
          level1Guess: indicator.level1Guess || '',
        });
      }
    }
  }
  return results;
};

export interface IsHumanResult {
  indicator: IndicatorDetails;
  level1IsHuman: string;
}

export const getBulkIsHumanIndicators = (
  impactDetails: SectionIndicators,
): IsHumanResult[] => {
  const reachIndicators = impactDetails.reach || [];
  const isHumanIndicators = reachIndicators.filter(
    indicator => indicator.isHuman === true,
  );

  // Only return the last indicator if there are 2 or more isHuman indicators
  if (isHumanIndicators.length < 2) {
    return [];
  }

  const lastIndicator = isHumanIndicators[isHumanIndicators.length - 1];
  return [
    {
      indicator: lastIndicator,
      level1IsHuman: lastIndicator.level1IsHuman || '',
    },
  ];
};

export interface ForecastResultOneResult {
  indicator: IndicatorDetails;
  sectionKey: IndicatorSectionKey;
  forecastValue: string;
  finalForecastValue: string;
  level1ForecastResultOne: string;
}

const FORECAST_RESULT_ONE_EXCLUDED_SECTIONS: IndicatorSectionKey[] = [
  'capacityDevelopment',
  'catalyzed',
];

export const isForecastResultOneMatch = (
  sectionKey: IndicatorSectionKey,
  forecastValue: string,
  finalForecastValue: string,
): boolean => {
  if (FORECAST_RESULT_ONE_EXCLUDED_SECTIONS.includes(sectionKey)) return false;
  return forecastValue === '1' || finalForecastValue === '1';
};

export const getBulkForecastResultOne = (
  impactDetails: SectionIndicators,
): ForecastResultOneResult[] => {
  const results: ForecastResultOneResult[] = [];
  for (const sectionKey of indicatorSectionsInOrder) {
    const indicators = impactDetails[sectionKey] || [];
    for (const indicator of indicators) {
      const forecast = replaceCommas(String(indicator.forecast || ''));
      const finalForecast = replaceCommas(
        String(indicator.finalForecast || ''),
      );

      if (isForecastResultOneMatch(sectionKey, forecast, finalForecast)) {
        results.push({
          indicator,
          sectionKey,
          forecastValue: forecast,
          finalForecastValue: finalForecast,
          level1ForecastResultOne: indicator.level1ForecastResultOne || '',
        });
      }
    }
  }
  return results;
};
